// 📄 components/tablas/TablaCatalogGrid.tsx
// Grid filtrable de tarjetas del Catálogo de Tablas SAP — extraído a
// pedido del usuario (2026-09-04: "el catálogo de tablas no tiene filtros
// así como el de las interfaces") para compartir la misma UI de
// consulta/filtro entre Admin (CRUD, ver TablaCatalogSection.tsx), Ariba
// Toolbox (solo lectura, ver TablaCatalogToolbox.tsx) y dentro de un
// proyecto (solo lectura, tanto para el catálogo estándar como para las
// Tablas Z propias del proyecto, ver TablasCatalogo.tsx). Mismo patrón
// exacto que InterfaceCatalogGrid.tsx — a diferencia de Interfaces, acá
// solo hay UNA dimensión de filtro real además del texto libre (Solución:
// el modelo de datos de una tabla no tiene objetoNegocio/plataforma/
// dirección), así que el filtro queda más chico a propósito, sin agregar
// dropdowns que no aplican.
import { useMemo, useState } from 'react';
import { Search, Pencil, Trash2 } from 'lucide-react';
import { TablaEstandarData } from '../../services/catalogoTablasService.ts';
import { SOLUCIONES_BADI, solucionBadiMeta } from '../../services/catalogoBadisService.ts';

// Item del grid: TablaEstandarData (catálogo global) o TablaZData (propia
// de un proyecto) — mismo shape exacto entre ambas, ver comentario de
// cabecera en tablaZService.ts. `esZ` se pasa a nivel de grid (no por
// item): TablasCatalogo.tsx siempre renderiza el catálogo estándar y las
// Tablas Z en 2 grids separados, nunca mezclados en uno solo.
export type TablaCatalogItem = TablaEstandarData & { id: string };

interface TablaCatalogGridProps {
  tablas: TablaCatalogItem[];
  esZ?: boolean;
  readOnly?: boolean;
  onEdit?: (item: TablaCatalogItem) => void;
  onDelete?: (item: TablaCatalogItem) => void;
}

export default function TablaCatalogGrid({ tablas, esZ = false, readOnly = false, onEdit, onDelete }: TablaCatalogGridProps) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return tablas.filter((t) => {
      if (filtroSolucion && t.solucion !== filtroSolucion) return false;
      if (!q) return true;
      return [t.nombreTecnico, t.transaccion, t.descripcion, ...t.campos.map((c) => `${c.nombre} ${c.descripcion}`)]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [tablas, busqueda, filtroSolucion]);

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre técnico, transacción o campo..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select className={inputClass} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)}>
          <option value="">Todas las soluciones</option>
          {SOLUCIONES_BADI.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {filtradas.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          {tablas.length === 0 ? 'Todavía no hay tablas registradas en el catálogo.' : 'Ninguna tabla coincide con el filtro.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtradas.map((t) => {
            const solMeta = solucionBadiMeta(t.solucion);
            return (
              <div key={t.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-sm font-semibold font-mono text-white truncate">{t.nombreTecnico}</span>
                    {t.transaccion && <span className="text-xs font-mono text-gray-400 flex-shrink-0">({t.transaccion})</span>}
                  </div>
                  {!readOnly && (onEdit || onDelete) && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {onEdit && (
                        <button onClick={() => onEdit(t)} className="text-gray-400 hover:text-white" title="Editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(t)} className="text-gray-500 hover:text-red-400" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {esZ && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                    {solMeta.label}
                  </span>
                </div>

                {t.descripcion && <p className="text-sm text-gray-300">{t.descripcion}</p>}

                {t.campos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {t.campos.map((c, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 border border-white/10 text-gray-300 font-mono" title={c.descripcion}>
                        {c.nombre}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
